// Rezepte.
//
// Die Zubereitung stand bisher zwar in der Datenbank und in der API, aber es
// gab kein einziges Eingabefeld dafür. Jetzt: Anleitung, Bild, Tags,
// Favoriten, Suche, Portionsskalierung, Kochmodus mit wachem Bildschirm,
// Import per URL und Export/Import als Datei.
import { formatNumber } from '@speis/shared';
import type { RecipeView } from '@speis/shared';
import { api } from '../core/api.js';
import { guard, guarded } from '../core/guard.js';
import { onLiveEvent } from '../core/sse.js';
import { state } from '../core/store.js';
import { clear, el, replace } from '../ui/dom.js';
import { confirmDialog, promptDialog } from '../ui/dialog.js';
import { icon } from '../ui/icons.js';
import { openSheet } from '../ui/sheet.js';
import { toast } from '../ui/toast.js';
import { skeletonList } from './shared.js';
import { openCookIdeas, openFeasibilityCheck } from '../flows/cookai.js';
import { openCookMode } from '../flows/cookmode.js';

let recipes: RecipeView[] = [];
let query = '';
let onlyFavorites = false;
let host: HTMLElement | null = null;
let unsubscribe: (() => void) | null = null;

export function renderRecipes(container: HTMLElement): void {
  host = container;
  unsubscribe?.();
  clear(container);
  container.append(skeletonList(4));
  void load();
  unsubscribe = onLiveEvent('recipes', () => void load());
}

export function teardownRecipes(): void {
  unsubscribe?.();
  unsubscribe = null;
  host = null;
}

async function load(): Promise<void> {
  const result = await guard(() => api<{ recipes: RecipeView[] }>('/api/recipes'), {
    failure: 'Rezepte konnten nicht geladen werden',
  });
  recipes = result?.recipes ?? [];
  draw();
}

function draw(): void {
  if (!host) return;
  clear(host);

  host.append(
    el('div', { class: 'recipe-tools' }, [
      el(
        'button',
        { class: 'btn btn-secondary', type: 'button', onclick: () => void openCookIdeas() },
        [icon('sparkles', { size: 18 }), 'Was kochen?'],
      ),
      el(
        'button',
        { class: 'btn btn-secondary', type: 'button', onclick: () => openFeasibilityCheck() },
        [icon('pot', { size: 18 }), 'Reicht es für …?'],
      ),
    ]),
  );

  const search = el('input', {
    class: 'input',
    type: 'search',
    placeholder: 'Rezepte durchsuchen …',
    value: query,
    'aria-label': 'Rezepte durchsuchen',
  });
  search.addEventListener('input', () => {
    query = search.value;
    drawList();
  });

  const favToggle = el(
    'button',
    {
      class: 'chip',
      type: 'button',
      'aria-pressed': String(onlyFavorites),
      onclick: () => {
        onlyFavorites = !onlyFavorites;
        favToggle.setAttribute('aria-pressed', String(onlyFavorites));
        drawList();
      },
    },
    [icon('star', { size: 15 }), 'Favoriten'],
  );

  host.append(
    el('div', { class: 'pantry-toolbar' }, [
      el('div', { class: 'search' }, [icon('search', { size: 18 }), search]),
      el('div', { class: 'chip-row' }, [favToggle]),
    ]),
    el('div', { class: 'recipe-list' }),
    el('div', { class: 'recipe-footer' }, [
      el(
        'button',
        { class: 'btn btn-primary btn-block', type: 'button', onclick: () => openEditor(null) },
        [icon('plus', { size: 18 }), 'Neues Rezept'],
      ),
      el('div', { class: 'btn-row' }, [
        el(
          'button',
          {
            class: 'btn btn-secondary btn-sm',
            type: 'button',
            onclick: () => void importFromUrl(),
          },
          [icon('link', { size: 16 }), 'Aus Webseite'],
        ),
        el(
          'button',
          {
            class: 'btn btn-secondary btn-sm',
            type: 'button',
            onclick: () => void exportRecipes(),
          },
          [icon('download', { size: 16 }), 'Export'],
        ),
        el(
          'button',
          { class: 'btn btn-secondary btn-sm', type: 'button', onclick: () => importRecipes() },
          [icon('upload', { size: 16 }), 'Import'],
        ),
      ]),
    ]),
  );

  drawList();
}

function drawList(): void {
  const container = host?.querySelector<HTMLElement>('.recipe-list');
  if (!container) return;

  const q = query.trim().toLowerCase();
  const visible = recipes
    .filter((r) => !onlyFavorites || r.favorite)
    .filter(
      (r) =>
        !q ||
        r.name.toLowerCase().includes(q) ||
        r.tags.some((t) => t.toLowerCase().includes(q)) ||
        r.ingredients.some((i) => (i.genericName ?? '').toLowerCase().includes(q)),
    );

  if (!visible.length) {
    replace(
      container,
      recipes.length
        ? el('div', { class: 'empty' }, [
            el('div', { class: 'empty-icon', 'aria-hidden': 'true' }, '🔍'),
            el('p', { class: 'empty-title' }, 'Kein Treffer'),
          ])
        : el('div', { class: 'empty' }, [
            el('div', { class: 'empty-icon', 'aria-hidden': 'true' }, '📖'),
            el('p', { class: 'empty-title' }, 'Noch keine Rezepte'),
            el(
              'p',
              { class: 'empty-text' },
              'Ein Rezept ist vor allem eine Zutatenliste – daraus rechnet die App Fehlmengen und bucht beim Kochen ab.',
            ),
          ]),
    );
    return;
  }

  const list = el('ul', { class: 'list' });
  for (const r of visible) list.append(recipeRow(r));
  replace(container, list);
}

function recipeRow(r: RecipeView): HTMLElement {
  const summary =
    r.ingredients
      .map((i) => i.genericName ?? state.products.find((p) => p.id === i.productId)?.name ?? '')
      .filter(Boolean)
      .slice(0, 6)
      .join(', ') || 'keine Zutaten';

  return el('li', { class: 'list-item' }, [
    r.imageUrl
      ? el('img', { class: 'item-thumb', src: r.imageUrl, alt: '', loading: 'lazy' })
      : el('span', { class: 'item-thumb thumb-fallback', 'aria-hidden': 'true' }, '🍲'),
    el('button', { class: 'item-body item-open', type: 'button', onclick: () => openDetail(r) }, [
      el('div', { class: 'item-name' }, r.name),
      el('div', { class: 'item-meta' }, [
        el('span', {}, `${r.servings} P.`),
        r.tags.length ? el('span', { class: 'badge' }, r.tags[0]!) : null,
        el('span', { class: 'ingredient-summary' }, summary),
      ]),
    ]),
    el(
      'button',
      {
        class: 'icon-btn',
        type: 'button',
        'aria-label': r.favorite ? 'Favorit entfernen' : 'Als Favorit merken',
        'aria-pressed': String(r.favorite),
        onclick: guarded(async () => {
          await api(`/api/recipes/${r.id}`, { method: 'PATCH', body: { favorite: !r.favorite } });
          await load();
        }),
      },
      icon('star', { size: 20 }),
    ),
  ]);
}

// ── Detailansicht ─────────────────────────────────────────────────────

function openDetail(r: RecipeView): void {
  let servings = r.servings;

  const factorNote = el('p', { class: 'tiny' });
  const ingredientList = el('ul', { class: 'ingredient-list' });
  const feasibility = el('div', { class: 'feasibility' });

  const drawIngredients = () => {
    const factor = servings / (r.servings || 1);
    clear(ingredientList);
    for (const i of r.ingredients) {
      const name =
        i.genericName ?? state.products.find((p) => p.id === i.productId)?.name ?? 'Zutat';
      const amount = i.amount != null ? formatNumber(i.amount * factor) : '';
      ingredientList.append(
        el('li', {}, [
          el(
            'span',
            { class: 'ing-amount tabular' },
            amount ? `${amount} ${i.unit ?? ''}`.trim() : '·',
          ),
          el('span', {}, name),
        ]),
      );
    }
    factorNote.textContent =
      servings === r.servings ? '' : `Mengen auf ${servings} Portionen umgerechnet.`;
  };

  const stepper = el('div', { class: 'stepper' }, [
    el(
      'button',
      {
        type: 'button',
        'aria-label': 'Weniger Portionen',
        onclick: () => setServings(servings - 1),
      },
      '−',
    ),
    el('span', { class: 'value', id: 'servings-value' }, String(servings)),
    el(
      'button',
      { type: 'button', 'aria-label': 'Mehr Portionen', onclick: () => setServings(servings + 1) },
      '+',
    ),
  ]);

  function setServings(next: number): void {
    servings = Math.max(1, Math.min(50, next));
    stepper.querySelector('#servings-value')!.textContent = String(servings);
    drawIngredients();
    void loadFeasibility();
  }

  async function loadFeasibility(): Promise<void> {
    replace(feasibility, el('span', { class: 'spinner' }));
    try {
      const result = await api<{
        feasible: boolean;
        sentence: string;
        missing: { name: string }[];
      }>(`/api/recipes/${r.id}/feasibility?servings=${servings}`);
      replace(feasibility, [
        el('p', { class: result.feasible ? 'notice' : 'notice notice-warn' }, result.sentence),
        !result.feasible && result.missing.length
          ? el(
              'button',
              {
                class: 'btn btn-secondary btn-block',
                type: 'button',
                onclick: guarded(
                  async () => {
                    await api('/api/feasibility/add-missing', {
                      method: 'POST',
                      body: { recipe: r.name, servings },
                    });
                  },
                  { success: 'Fehlendes steht auf der Einkaufsliste' },
                ),
              },
              [icon('cart', { size: 18 }), 'Fehlendes auf die Liste'],
            )
          : null,
      ]);
    } catch {
      replace(feasibility, el('p', { class: 'tiny' }, 'Machbarkeit gerade nicht prüfbar.'));
    }
  }

  const body = el('div', {}, [
    r.imageUrl ? el('img', { class: 'recipe-hero', src: r.imageUrl, alt: '' }) : null,
    r.tags.length
      ? el(
          'div',
          { class: 'chip-row' },
          r.tags.map((t) => el('span', { class: 'badge' }, t)),
        )
      : null,
    el('div', { class: 'field' }, [el('label', {}, 'Portionen'), stepper, factorNote]),
    el('h3', { class: 'eyebrow' }, 'Zutaten'),
    ingredientList,
    feasibility,
    r.instructions
      ? el('div', {}, [
          el('h3', { class: 'eyebrow' }, 'Zubereitung'),
          el('div', { class: 'instructions' }, r.instructions),
        ])
      : null,
    r.sourceUrl
      ? el('p', { class: 'tiny' }, [
          'Quelle: ',
          el('a', { href: r.sourceUrl, target: '_blank', rel: 'noreferrer noopener' }, r.sourceUrl),
        ])
      : null,
  ]);

  const sheet = openSheet(body, { title: r.name });
  drawIngredients();
  void loadFeasibility();

  sheet.addActions(
    el(
      'button',
      {
        class: 'btn btn-primary btn-block',
        type: 'button',
        onclick: guarded(
          async () => {
            const result = await api<{ shortfalls: { name: string }[] }>(
              `/api/recipes/${r.id}/cook`,
              { method: 'POST', body: { servings } },
            );
            sheet.close();
            toast(
              result.shortfalls.length
                ? `Abgebucht – knapp war: ${result.shortfalls.map((s) => s.name).join(', ')}`
                : 'Zutaten abgebucht',
            );
          },
          { failure: 'Abbuchen fehlgeschlagen' },
        ),
      },
      [icon('pot', { size: 18 }), 'Gekocht & abbuchen'],
    ),
    r.instructions
      ? el(
          'button',
          {
            class: 'btn btn-secondary btn-block',
            type: 'button',
            onclick: () => {
              sheet.close();
              openCookMode(r, servings);
            },
          },
          [icon('flame', { size: 18 }), 'Kochmodus'],
        )
      : null,
    el(
      'button',
      {
        class: 'btn btn-secondary btn-block',
        type: 'button',
        onclick: () => {
          sheet.close();
          openEditor(r);
        },
      },
      [icon('edit', { size: 18 }), 'Bearbeiten'],
    ),
  );
}

// ── Editor ────────────────────────────────────────────────────────────

interface IngredientRow {
  productId: number | null;
  name: string;
  amount: string;
  unit: string;
}

const UNITS = [
  '',
  'g',
  'kg',
  'ml',
  'l',
  'Stück',
  'EL',
  'TL',
  'Prise',
  'Bund',
  'Dose',
  'Packung',
  'Becher',
  'Scheibe',
];

export function openEditor(recipe: RecipeView | null): void {
  const isNew = !recipe;

  const name = el('input', {
    class: 'input',
    type: 'text',
    value: recipe?.name ?? '',
    placeholder: 'Rezeptname',
  });
  const servings = el('input', {
    class: 'input',
    type: 'number',
    min: 1,
    max: 50,
    value: recipe?.servings ?? 2,
  });
  const prep = el('input', {
    class: 'input',
    type: 'number',
    min: 0,
    placeholder: 'Minuten',
  });
  const instructions = el(
    'textarea',
    {
      class: 'textarea',
      placeholder: '1. Zwiebeln würfeln …\n2. Anbraten …',
    },
    recipe?.instructions ?? '',
  );
  const imageUrl = el('input', {
    class: 'input',
    type: 'url',
    value: recipe?.imageUrl ?? '',
    placeholder: 'Bild-Adresse (optional)',
  });
  const tags = el('input', {
    class: 'input',
    type: 'text',
    value: recipe?.tags.join(', ') ?? '',
    placeholder: 'z.B. schnell, vegetarisch',
  });

  const rows: IngredientRow[] = (recipe?.ingredients ?? []).map((i) => ({
    productId: i.productId,
    name: i.genericName ?? state.products.find((p) => p.id === i.productId)?.name ?? '',
    amount: i.amount != null ? String(i.amount) : '',
    unit: i.unit ?? '',
  }));

  const ingredientWrap = el('div', { class: 'ing-editor' });

  function drawRows(): void {
    clear(ingredientWrap);
    rows.forEach((row, index) => ingredientWrap.append(ingredientRow(row, index)));
    ingredientWrap.append(
      el(
        'button',
        {
          class: 'btn btn-secondary btn-sm',
          type: 'button',
          onclick: () => {
            rows.push({ productId: null, name: '', amount: '', unit: '' });
            drawRows();
          },
        },
        [icon('plus', { size: 16 }), 'Zutat'],
      ),
    );
  }

  function ingredientRow(row: IngredientRow, index: number): HTMLElement {
    const amount = el('input', {
      class: 'input ing-amount-input',
      type: 'text',
      inputmode: 'decimal',
      value: row.amount,
      placeholder: 'Menge',
      'aria-label': 'Menge',
    });
    amount.addEventListener('input', () => {
      row.amount = amount.value;
    });

    const unit = el('select', { class: 'select ing-unit', 'aria-label': 'Einheit' });
    for (const u of UNITS) {
      const option = el('option', { value: u }, u || '—');
      if (u === row.unit) option.selected = true;
      unit.append(option);
    }
    unit.addEventListener('change', () => {
      row.unit = unit.value;
    });

    const nameInput = el('input', {
      class: 'input',
      type: 'text',
      value: row.name,
      placeholder: 'Zutat',
      'aria-label': 'Zutat',
      list: 'product-names',
    });
    nameInput.addEventListener('input', () => {
      row.name = nameInput.value;
      // Freitext bricht die Produktbindung – sonst zeigt die Zeile eine Sache
      // an und bucht eine andere ab.
      const exact = state.products.find(
        (p) => p.name.toLowerCase() === nameInput.value.trim().toLowerCase(),
      );
      row.productId = exact?.id ?? null;
      link.setAttribute('aria-pressed', String(Boolean(row.productId)));
    });

    const link = el(
      'button',
      {
        class: 'icon-btn',
        type: 'button',
        'aria-label': 'Mit Produkt verknüpfen',
        'aria-pressed': String(Boolean(row.productId)),
        onclick: () =>
          openProductPicker((product) => {
            row.productId = product.id;
            row.name = product.name;
            nameInput.value = product.name;
            link.setAttribute('aria-pressed', 'true');
          }),
      },
      icon('link', { size: 18 }),
    );

    const remove = el(
      'button',
      {
        class: 'icon-btn',
        type: 'button',
        'aria-label': 'Zutat entfernen',
        onclick: () => {
          rows.splice(index, 1);
          drawRows();
        },
      },
      icon('x', { size: 18 }),
    );

    return el('div', { class: 'ing-row' }, [amount, unit, nameInput, link, remove]);
  }

  const body = el('div', {}, [
    el('div', { class: 'field' }, [el('label', {}, 'Name'), name]),
    el('div', { class: 'grid-2' }, [
      el('div', { class: 'field' }, [el('label', {}, 'Portionen'), servings]),
      el('div', { class: 'field' }, [el('label', {}, 'Zubereitungszeit'), prep]),
    ]),
    el('div', { class: 'field' }, [el('label', {}, 'Zutaten'), ingredientWrap]),
    el('div', { class: 'field' }, [
      el('label', {}, 'Zubereitung'),
      instructions,
      el(
        'p',
        { class: 'field-hint' },
        'Schritt für Schritt – der Kochmodus zeigt sie später einzeln groß an.',
      ),
    ]),
    el('div', { class: 'field' }, [el('label', {}, 'Schlagwörter'), tags]),
    el('div', { class: 'field' }, [el('label', {}, 'Bild'), imageUrl]),
    // Vorschlagsliste für Zutatennamen aus dem eigenen Katalog.
    (() => {
      const dl = el('datalist', { id: 'product-names' });
      for (const p of state.products) dl.append(el('option', { value: p.name }));
      return dl;
    })(),
  ]);

  const sheet = openSheet(body, { title: isNew ? 'Neues Rezept' : 'Rezept bearbeiten' });
  drawRows();

  sheet.addActions(
    el(
      'button',
      {
        class: 'btn btn-primary btn-block',
        type: 'button',
        onclick: guarded(
          async () => {
            if (!name.value.trim()) {
              toast('Bitte einen Namen eintragen');
              return;
            }
            const payload = {
              name: name.value.trim(),
              servings: Number(servings.value) || 2,
              prepMinutes: prep.value ? Number(prep.value) : null,
              instructions: instructions.value.trim() || null,
              imageUrl: imageUrl.value.trim() || null,
              tags: tags.value
                .split(',')
                .map((t) => t.trim())
                .filter(Boolean),
              ingredients: rows
                .filter((r) => r.productId || r.name.trim())
                .map((r) => ({
                  productId: r.productId,
                  genericName: r.productId ? null : r.name.trim(),
                  amount: r.amount.trim() === '' ? null : Number(r.amount.replace(',', '.')),
                  unit: r.unit || null,
                })),
            };
            if (isNew) await api('/api/recipes', { method: 'POST', body: payload });
            else await api(`/api/recipes/${recipe.id}`, { method: 'PATCH', body: payload });
            sheet.close();
            await load();
          },
          { success: 'Gespeichert', failure: 'Speichern fehlgeschlagen' },
        ),
      },
      'Speichern',
    ),
    !isNew
      ? el(
          'button',
          {
            class: 'btn btn-danger btn-block',
            type: 'button',
            onclick: async () => {
              const ok = await confirmDialog({
                title: `„${recipe.name}" löschen?`,
                confirmLabel: 'Löschen',
                danger: true,
              });
              if (!ok) return;
              await guard(
                async () => {
                  await api(`/api/recipes/${recipe.id}`, { method: 'DELETE' });
                  sheet.close();
                  await load();
                },
                { success: 'Gelöscht' },
              );
            },
          },
          'Löschen',
        )
      : null,
  );
}

export function openProductPicker(
  onPick: (product: (typeof state.products)[number]) => void,
): void {
  const search = el('input', { class: 'input', type: 'search', placeholder: 'Produkt suchen …' });
  const results = el('div', { class: 'results' });
  const body = el('div', {}, [
    el('div', { class: 'search' }, [icon('search', { size: 18 }), search]),
    results,
  ]);
  const sheet = openSheet(body, { title: 'Produkt verknüpfen' });

  const drawResults = () => {
    const q = search.value.trim().toLowerCase();
    const hits = state.products
      .filter((p) => !q || `${p.name} ${p.brand ?? ''}`.toLowerCase().includes(q))
      .slice(0, 40);
    clear(results);
    for (const p of hits) {
      results.append(
        el(
          'button',
          {
            class: 'result',
            type: 'button',
            onclick: () => {
              onPick(p);
              sheet.close();
            },
          },
          p.brand ? `${p.name} · ${p.brand}` : p.name,
        ),
      );
    }
    if (!hits.length) results.append(el('p', { class: 'tiny' }, 'Kein Produkt gefunden.'));
  };

  search.addEventListener('input', drawResults);
  drawResults();
}

// ── Import & Export ───────────────────────────────────────────────────

async function importFromUrl(): Promise<void> {
  const url = await promptDialog({
    title: 'Rezept aus Webseite',
    label: 'Adresse der Rezeptseite',
    placeholder: 'https://…',
    confirmLabel: 'Holen',
  });
  if (!url) return;

  const result = await guard(
    () =>
      api<{ recipe: RecipeView & { via: string; prepMinutes: number | null } }>(
        '/api/recipes/import',
        { method: 'POST', body: { url } },
      ),
    { failure: 'Import fehlgeschlagen' },
  );
  if (!result) return;

  toast(
    result.recipe.via === 'json-ld'
      ? 'Rezeptdaten direkt von der Seite gelesen'
      : 'Von der KI aus dem Seitentext gelesen – bitte prüfen',
  );
  // Als Vorschlag öffnen: gespeichert wird erst nach Durchsehen.
  openEditor({ ...result.recipe, id: 0, favorite: false } as RecipeView);
}

async function exportRecipes(): Promise<void> {
  const data = await guard(() => api<unknown>('/api/recipes/export'), {
    failure: 'Export fehlgeschlagen',
  });
  if (!data) return;

  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = el('a', { href: url, style: 'display:none' });
  link.download = `speis-rezepte-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
  toast('Rezepte exportiert');
}

function importRecipes(): void {
  const input = el('input', { type: 'file', accept: 'application/json', style: 'display:none' });
  document.body.append(input);
  input.addEventListener('change', () => {
    const file = input.files?.[0];
    input.remove();
    if (!file) return;
    void guard(
      async () => {
        const parsed = JSON.parse(await file.text()) as { recipes?: RecipeView[] };
        if (!Array.isArray(parsed.recipes)) throw new Error('Datei enthält keine Rezepte.');
        const { imported } = await api<{ imported: number }>('/api/recipes/import-json', {
          method: 'POST',
          body: { recipes: parsed.recipes },
        });
        toast(`${imported} Rezepte importiert`);
        await load();
      },
      { failure: 'Import fehlgeschlagen' },
    );
  });
  input.click();
}

export function reloadRecipes(): Promise<void> {
  return load();
}
